import React, { useCallback, useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import Logger from "../../services/Logger";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import Item from "../components/Item";
import SystemLogService from "../setting/syslogs/SystemLogService";
import {
    ADMIN_ADVERTISEMENT_PROMOTION_NEW_PATH,
    promotionEditPath,
} from "./advertisementRoutes";
import PromotionEmailService from "./PromotionEmailService";
import PromotionSendLogDialog from "./PromotionSendLogDialog";
import WeeklyIssuesPanel from "./WeeklyIssuesPanel";
import {
    buildDuplicatePayload,
    isIdleSchedulerLog,
    logAppLabel,
    logEmailLabel,
    LOG_SOURCE,
    logTitleLabel,
    normalizeStage,
    normalizeStatus,
    promotionAppLabel,
    stageChipColor,
    statusChipColor,
} from "./promotionEmailUi";

const TAB_PROMOTION = "promotion";
const TAB_LOG = "log";
const TAB_WEEKLY = "weekly";
const TAB_STORAGE_KEY = "admin.advertisement.promotion.tab";
const TAB_VALUES = [TAB_PROMOTION, TAB_LOG, TAB_WEEKLY];

function loadStoredTab() {
    try {
        const v = localStorage.getItem(TAB_STORAGE_KEY);
        if (TAB_VALUES.includes(v)) return v;
    } catch {
        /* ignore */
    }
    return TAB_PROMOTION;
}

function persistTab(tab) {
    try {
        localStorage.setItem(TAB_STORAGE_KEY, tab);
    } catch {
        /* ignore */
    }
}

function formatUpdatedAt(value) {
    if (value == null || value === "") return "—";
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return dayjs(n).format("YYYY-MM-DD HH:mm");
}

export default function PromotionMain() {
    const navigate = useNavigate();
    const location = useLocation();
    const [promotions, setPromotions] = useState([]);
    const [tabValue, setTabValue] = useState(loadStoredTab);
    const [listLoading, setListLoading] = useState(false);
    const [logRows, setLogRows] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [busyAction, setBusyAction] = useState(null);
    const [busyCode, setBusyCode] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);
    const [logPromotion, setLogPromotion] = useState(null);
    const [sendLogOpen, setSendLogOpen] = useState(false);

    const showSnackbar = (severity, message) => {
        setSnackbar({ open: true, severity, message });
    };

    const loadPromotions = useCallback(async () => {
        setListLoading(true);
        try {
            const rows = await PromotionEmailService.list();
            setPromotions(rows);
        } catch (err) {
            Logger.error("List promotion emails:", err);
            showSnackbar("error", typeof err === "string" ? err : err?.message || "Failed to load promotions");
        } finally {
            setListLoading(false);
        }
    }, []);

    const loadSendLogs = useCallback(async () => {
        setLogsLoading(true);
        try {
            const result = await SystemLogService.list(0, 25, "createdAt:desc", null, LOG_SOURCE, false);
            if (result.success && result.data) {
                const content = Array.isArray(result.data.content) ? result.data.content : [];
                setLogRows(content.filter((row) => !isIdleSchedulerLog(row)));
            }
        } catch (err) {
            Logger.error("Promotion send logs:", err);
        } finally {
            setLogsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPromotions();
        loadSendLogs();
    }, [loadPromotions, loadSendLogs]);

    useEffect(() => {
        const msg = location.state?.message;
        if (msg) {
            showSnackbar("success", msg);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.message, location.pathname, navigate]);

    useEffect(() => {
        if (location.state?.weeklyTab) {
            setTabValue(TAB_WEEKLY);
            persistTab(TAB_WEEKLY);
        }
    }, [location.state?.weeklyTab]);

    const handleTabChange = (_event, nextTab) => {
        setTabValue(nextTab);
        persistTab(nextTab);
        if (nextTab === TAB_LOG) {
            loadSendLogs();
        }
    };

    const handleDuplicate = async (row) => {
        if (!row?.code || busyAction) return;
        setBusyAction("duplicate");
        setBusyCode(row.code);
        try {
            let source = row;
            if (!source.htmlContent) {
                const loaded = await PromotionEmailService.getByCode(row.code);
                if (loaded) source = loaded;
            }
            const payload = buildDuplicatePayload(source);
            const response = await PromotionEmailService.save(payload);
            if (response && response.code === "FAILURE") {
                showSnackbar("error", response.message || "Failed to duplicate");
                return;
            }
            const nextCode = response?.code || payload.code;
            navigate(promotionEditPath(nextCode), { state: { message: "Duplicated as draft." } });
        } catch (err) {
            Logger.error("Duplicate promotion email:", err);
            showSnackbar("error", err.message || err || "Failed to duplicate");
        } finally {
            setBusyAction(null);
            setBusyCode("");
        }
    };

    const handleDeleteConfirm = async (codeToDelete) => {
        const nextCode = codeToDelete || rowToDelete?.code;
        if (!nextCode) return;
        setBusyAction("delete");
        setBusyCode(nextCode);
        try {
            await PromotionEmailService.remove(nextCode);
            showSnackbar("success", "Promotion and send logs deleted.");
            setRowToDelete(null);
            await loadPromotions();
            await loadSendLogs();
        } catch (err) {
            Logger.error("Delete promotion email:", err);
            showSnackbar("error", err.message || err || "Failed to delete promotion");
        } finally {
            setBusyAction(null);
            setBusyCode("");
        }
    };

    const columns = [
        {
            field: "stage",
            headerName: "Stage",
            width: 110,
            renderCell: (params) => {
                const stage = normalizeStage(params.value);
                return <Chip size="small" label={stage} color={stageChipColor(stage)} />;
            },
        },
        {
            field: "status",
            headerName: "Status",
            width: 150,
            renderCell: (params) => {
                const status = normalizeStatus(params.value);
                return <Chip size="small" label={status} color={statusChipColor(status)} />;
            },
        },
        {
            field: "name",
            headerName: "Name",
            flex: 1.2,
            minWidth: 200,
            valueGetter: (value, row) => value || row.code || "",
        },
        {
            field: "app",
            headerName: "App",
            flex: 0.8,
            minWidth: 160,
            valueGetter: (value) => promotionAppLabel(value) || "—",
        },
        {
            field: "createdAt",
            headerName: "Created",
            width: 160,
            valueFormatter: (value) => formatUpdatedAt(value),
        },
        {
            field: "updatedAt",
            headerName: "Updated",
            width: 160,
            valueFormatter: (value) => formatUpdatedAt(value),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 140,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params.row;
                const rowBusy = busyAction != null && busyCode === row.code;
                return (
                    <Box
                        sx={{ display: "flex", alignItems: "center", height: "100%" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Tooltip title="Duplicate">
                            <span>
                                <IconButton
                                    size="small"
                                    aria-label="Duplicate promotion"
                                    disabled={busyAction != null}
                                    onClick={() => handleDuplicate(row)}
                                >
                                    {rowBusy && busyAction === "duplicate" ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <ContentCopyIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Log">
                            <span>
                                <IconButton
                                    size="small"
                                    aria-label="Promotion send log"
                                    onClick={() => {
                                        setLogPromotion(row);
                                        setSendLogOpen(true);
                                    }}
                                >
                                    <ListAltIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <span>
                                <IconButton
                                    size="small"
                                    aria-label="Delete promotion"
                                    color="error"
                                    disabled={busyAction != null}
                                    onClick={() => {
                                        setRowToDelete(row);
                                        setConfirmDeleteOpen(true);
                                    }}
                                >
                                    {rowBusy && busyAction === "delete" ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <DeleteOutlineIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
                <Tab value={TAB_PROMOTION} label="Promotions" />
                <Tab value={TAB_LOG} label="Log" />
                <Tab value={TAB_WEEKLY} label="Weekly email" />
            </Tabs>
            <Grid container spacing={2}>
                {tabValue === TAB_PROMOTION ? (
                    <Grid size={{ xs: 12 }}>
                        <Item>
                            <Card>
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 1,
                                            flexWrap: "wrap",
                                            mb: 2,
                                        }}
                                    >
                                        <Typography variant="h6" component="h2">
                                            Promotions
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => navigate(ADMIN_ADVERTISEMENT_PROMOTION_NEW_PATH)}
                                            sx={{ textTransform: "none" }}
                                        >
                                            Create
                                        </Button>
                                    </Box>
                                    <DataGrid
                                        rows={promotions}
                                        columns={columns}
                                        getRowId={(row) => row.code || row.id}
                                        loading={listLoading}
                                        autoHeight
                                        pageSizeOptions={[10, 25, 50]}
                                        initialState={{
                                            pagination: { paginationModel: { pageSize: 25 } },
                                        }}
                                        disableRowSelectionOnClick
                                        onRowClick={(params) => {
                                            if (params.row?.code) {
                                                navigate(promotionEditPath(params.row.code));
                                            }
                                        }}
                                        localeText={{
                                            noRowsLabel: listLoading ? "Loading…" : "No promotions yet.",
                                        }}
                                        sx={{ minHeight: 360, "& .MuiDataGrid-row": { cursor: "pointer" } }}
                                    />
                                </CardContent>
                            </Card>
                        </Item>
                    </Grid>
                ) : null}
                {tabValue === TAB_LOG ? (
                    <Grid size={{ xs: 12 }}>
                        <Item>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" component="h3" gutterBottom>
                                        Send log
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Each recipient is stored in system_log with source {LOG_SOURCE}.
                                    </Typography>
                                    <DataGrid
                                        rows={logRows}
                                        columns={[
                                            {
                                                field: "message",
                                                headerName: "Message",
                                                flex: 1.2,
                                                minWidth: 220,
                                            },
                                            {
                                                field: "email",
                                                headerName: "Email",
                                                flex: 1,
                                                minWidth: 180,
                                                valueGetter: (value, row) => logEmailLabel(row),
                                            },
                                            {
                                                field: "title",
                                                headerName: "Title",
                                                flex: 1,
                                                minWidth: 180,
                                                valueGetter: (value, row) => logTitleLabel(row),
                                            },
                                            {
                                                field: "app",
                                                headerName: "App",
                                                flex: 0.8,
                                                minWidth: 160,
                                                valueGetter: (value, row) => logAppLabel(row, promotions),
                                            },
                                            {
                                                field: "createdAt",
                                                headerName: "Sent",
                                                width: 170,
                                                valueFormatter: (value) =>
                                                    value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "—",
                                            },
                                        ]}
                                        loading={logsLoading}
                                        autoHeight
                                        hideFooter
                                        disableRowSelectionOnClick
                                        localeText={{
                                            noRowsLabel: logsLoading ? "Loading…" : "No sends yet.",
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        </Item>
                    </Grid>
                ) : null}
                {tabValue === TAB_WEEKLY ? (
                    <Grid size={{ xs: 12 }}>
                        <WeeklyIssuesPanel openTemplate={location.state?.weeklyTab === "template"} />
                    </Grid>
                ) : null}
            </Grid>
            <PromotionSendLogDialog
                open={sendLogOpen}
                onClose={() => setSendLogOpen(false)}
                promotion={logPromotion}
            />
            <ConfirmDialog
                open={confirmDeleteOpen}
                setOpen={setConfirmDeleteOpen}
                title="Delete promotion"
                targetName={rowToDelete?.name || rowToDelete?.code || ""}
                targetId={rowToDelete?.code}
                message="Delete this promotion and its send logs? The mailing list is not changed."
                okText="Delete"
                cancelText="Cancel"
                callback={handleDeleteConfirm}
            />
            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((prev) => ({ ...prev, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
